import {
    GrowthBookProvider as InternalGrowthBookProvider,
    type GrowthBook,
} from "@growthbook/growthbook-react";
import * as React from "react";
import {useEffect} from "react";

import {useInterval} from "@khanacademy/wonder-blocks-timing";
import {getGrowthBook} from "@khan/analytics-growthbook";
import Log, {Errors} from "@khan/logging";
import {
    DeployInfo,
    PermissionsInfo,
    RequestInfo,
    useUserAttributes,
    type UserAttributes,
} from "@khan/shared-core";
import {useLocation} from "@khan/urls";

interface Props {
    children: React.ReactNode;
}

export default function GrowthBookProvider({
    children,
}: Props): React.ReactElement {
    const [growthbook, setGrowthBook] = React.useState<GrowthBook | undefined>(
        undefined,
    );
    const location = useLocation();
    const profile = useUserAttributes();

    useEffect(() => {
        // NOTE: We don't load GrowthBook inside of CS programs because
        // the GrowthBook tries to make a request to /api/features/<clientKey>
        // which is not available on kasandbox.org.
        // TODO(FEI-5337): Update kasandbox.org to support this endpoint.
        // NOTE: The reason why we don't check for `RequestInfo.embedded` is
        // that the URLs used by the iframe on CS program pages don't use
        // `?embedded=1` in the URL.
        if (location.origin.includes(".kasandbox.org")) {
            return;
        }

        const growthbook = getGrowthBook(location.origin, profile);

        // Initialize with a bunch of data from our info objects. We fallback
        // to using these if there is no user profile (e.g. the user is a
        // pre-phantom)
        growthbook.setAttributes(getAttributes(profile));

        // Load features asynchronously when the app renders
        growthbook.loadFeatures({autoRefresh: true});

        setGrowthBook(growthbook);
    }, [location.origin, profile]);

    useInterval(
        () => growthbook?.refreshFeatures({skipCache: true}),
        5 * 60 * 1000, // Refresh features every 5 minutes
    );

    return (
        <InternalGrowthBookProvider growthbook={growthbook}>
            {children}
        </InternalGrowthBookProvider>
    );
}

// Returns an object with the attributes we want GrowthBook to use when
// computing the values of feature flags.
const getAttributes = (profile: UserAttributes | null): Record<string, any> => {
    const attributes: any = {
        ...PermissionsInfo,
        ...RequestInfo,
        ...DeployInfo,
        isMobileApp: false,
    };
    attributes.kaid = globalThis.__GROWTHBOOK_HASHED_KAID__;

    if (!globalThis.__GROWTHBOOK_HASHED_KAID__) {
        Log.error(
            "Expected __GROWTHBOOK_HASHED_KAID__ to have a value",
            Errors.Internal,
            {
                sentryData: {
                    tags: {
                        growthbook: "true",
                    },
                },
            },
        );
    }

    if (profile) {
        const userDistrictInfos = profile.userDistrictInfos ?? [];
        const districtIds: Array<string> = userDistrictInfos.map(
            (info) => info.district.id,
        );

        Object.assign(attributes, {
            districtIds,
            isParent: profile.isParent,
            isTeacher: profile.isTeacher,
            hasChildStudents: profile.hasChildren,
            actorIsImpersonatingUser: profile.actorIsImpersonatingUser,
            includesDistrictOwnedData: profile.includesDistrictOwnedData,
            isAIGuideEnabled: profile.isAIGuideEnabled,
            hasAccessToAIGuideDev: profile.hasAccessToAIGuideDev,
            underAgeGate: !!profile.underAgeGate,
            hasCoach: profile.hasCoach,
            hasClasses: profile.hasClasses,
            isDeveloper: profile.isDeveloper,
            isPublisher: profile.isPublisher,
        });
    }

    return attributes;
};
